import React from 'react';

import {
	CircularProgress,
	IconButton,
	ListItem,
	ListItemIcon,
	ListItemText,
	Stack,
	styled,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { RiArrowDownLine, RiArrowUpLine, RiCloseLine, RiStore2Line } from 'react-icons/ri';
import { Location } from 'common/types';
import { useTranslation } from 'services/localization';
import { getPlanPricingText } from '../../utils';
import { PlanDefinitions, PurchasedPlan, ShopFeatures } from 'common/modules/plans';
import { ShopBilling } from 'common/modules/billing';

interface Props {
	location: Location;
	mainLocationId: string;
	shopFeatures: ShopFeatures | undefined;
	locationToDelete: string | null;
	onDelete: (location: Location) => void;
	deleteDisabled?: boolean;
	shopPlan: PurchasedPlan;
	shopBilling: ShopBilling | undefined;
	onMoveUp: () => void;
	onMoveDown: () => void;
}

const StoreTableRow = ({
	location,
	mainLocationId,
	shopFeatures,
	locationToDelete,
	onDelete,
	deleteDisabled,
	shopPlan,
	shopBilling,
	onMoveUp,
	onMoveDown,
}: Props) => {
	const { t } = useTranslation();

	const getPlanName = () => {
		const planDefinition = PlanDefinitions[shopPlan.plan];

		return planDefinition.name;
	};

	return (
		<TableRow>
			<CustomCell sx={{ pl: 0 }}>
				<ListItem>
					<ListItemIcon sx={{ minWidth: 42, color: (theme) => theme.palette.primary.dark }}>
						<RiStore2Line color="inherit" />
					</ListItemIcon>
					<ListItemText
						primary={location.name}
						secondary={
							location.id === mainLocationId
								? t('account.stores.defaultLocation', 'Default location')
								: ''
						}
					/>
				</ListItem>
			</CustomCell>
			<CustomCell>
				<Typography variant="body1">{getPlanName()}</Typography>
			</CustomCell>

			<CustomCell>
				<Typography variant="body1">
					{shopBilling?.billingMethod === 'custom'
						? t('common:billing.billingCycleCustom')
						: getPlanPricingText(shopFeatures?.plan, location.id)}
				</Typography>
			</CustomCell>

			<CustomCell align="right">
				{locationToDelete === location.id ? (
					<Stack direction="row" justifyContent="center">
						<CircularProgress size={20} color="primary" />
					</Stack>
				) : (
					<Stack direction="row" justifyContent="flex-end">
						<Tooltip title={t('common:actions.moveUp', 'Move up')}>
							<IconButton onClick={onMoveUp}>
								<RiArrowUpLine />
							</IconButton>
						</Tooltip>
						<Tooltip title={t('common:actions.moveDown', 'Move down')}>
							<IconButton onClick={onMoveDown}>
								<RiArrowDownLine />
							</IconButton>
						</Tooltip>
						<Tooltip title={t('common:actions.delete', 'Delete')}>
							<IconButton
								disabled={location.id === mainLocationId || deleteDisabled}
								onClick={() => onDelete(location)}
							>
								<RiCloseLine />
							</IconButton>
						</Tooltip>
					</Stack>
				)}
			</CustomCell>
		</TableRow>
	);
};

const CustomCell = styled(TableCell)(({ theme }) => ({
	borderBottom: `1px solid ${theme.palette.border.table}`,
}));

export default StoreTableRow;
